import {
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import { AuthService, UsersService } from 'src/app/api/identity/services';
import { LocalStorageService } from '../../shared/services/local-storage.service';
import { LoginApiResponse, UserProfileDto } from 'src/app/api/identity/models';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { RedirectService } from 'src/app/shared/services/redirect-service';
import { PrivilegeAcknowledgementService } from 'src/app/shared/services/privilege-acknowledgement.service';
import { LicenseProfileRedirectService } from 'src/app/shared/services/license-profile-redirect.service';
import { AppCacheService } from 'src/app/shared/services/app-cache.service';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  BrandingService,
  BrandProfile,
} from 'src/app/shared/branding/branding.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatButtonModule],
})
export class LoginComponent implements OnInit, OnDestroy {
  @HostBinding('class') class = 'w-100';
  public loginForm!: UntypedFormGroup;
  public isAuthLoading = false;
  loggedInUser: UserProfileDto = {};
  readonly brand: BrandProfile;
  constructor(
    private renderer: Renderer2,
    private toastr: ToastrService,
    private authService: AuthService,
    private router: Router,
    private localStorageService: LocalStorageService,
    private redirectService: RedirectService,
    private userService: UsersService,
    private acknowledgementService: PrivilegeAcknowledgementService,
    private licenseRedirect: LicenseProfileRedirectService,
    private appCache: AppCacheService,
    branding: BrandingService
  ) {
    this.brand = branding.brand;
  }
  ngOnInit() {
    this.renderer.addClass(document.querySelector('app-root'), 'login-page');
    this.loginForm = new UntypedFormGroup({
      email: new UntypedFormControl(null, Validators.required),
      password: new UntypedFormControl(null, Validators.required),
    });
  }
  private clearCredentialFields(): void {
    this.loginForm.patchValue({ email: null, password: null });
    this.loginForm.markAsPristine();
    this.loginForm.markAsUntouched();
  }

  private navigateAfterLogin() {
    const pendingAcknowledgement =
      this.acknowledgementService.isAcknowledgementRequired;
    this.licenseRedirect
      .shouldOpenProfile(pendingAcknowledgement)
      .subscribe(openProfile => {
        const privilegePath = this.acknowledgementService.viewPath();
        if (!openProfile && pendingAcknowledgement && privilegePath) {
          this.isAuthLoading = false;
          this.redirectService.clearRedirectUrl();
          this.acknowledgementService.initialRedirectDone = true;
          void this.router.navigateByUrl(privilegePath);
          return;
        }
        if (openProfile) {
          this.isAuthLoading = false;
          this.redirectService.clearRedirectUrl();
          void this.router.navigate(['/profile']);
          return;
        }
        this.goToPostLoginDestination();
      });
  }

  private goToPostLoginDestination() {
    this.isAuthLoading = false;
    const redirectUrl = this.redirectService.getRedirectUrl();
    if (redirectUrl) {
      void this.router.navigateByUrl(redirectUrl);
      this.redirectService.clearRedirectUrl();
      return;
    }
    void this.router.navigate(['/']);
  }

  fetchLoggedInUser() {
    this.userService.getLoggedInUserDetails().subscribe({
      next: user => {
        this.loggedInUser = user?.data?.[0] || {};
        this.redirectService.setPermissions(this.loggedInUser.Privileges || []);
        this.appCache.invalidate();
        this.licenseRedirect.reset();
        this.acknowledgementService.reset();
        this.acknowledgementService.setExplicitUserId(this.loggedInUser.id);
        this.acknowledgementService.ensureLoaded().subscribe({
          next: () => this.navigateAfterLogin(),
          error: () => this.navigateAfterLogin(),
        });
      },
      error: error => {
        this.isAuthLoading = false;
        this.toastr.error(error?.error?.message);
      },
    });
  }

  loginByAuth() {
    if (this.loginForm.valid) {
      this.isAuthLoading = true;
      const payload = {
        body: {
          username: this.loginForm.value.email,
          password: this.loginForm.value.password,
        },
      };
      this.authService.passwordLogin(payload).subscribe({
        next: (res: LoginApiResponse) => {
          if (res.code !== 200) {
            this.isAuthLoading = false;
            this.clearCredentialFields();
            this.toastr.error(res.message || 'Login failed.');
            return;
          }
          this.toastr.success(res.message);
          const token = res.data ? (res.data[0].accessToken as string) : '';
          const refresh = res.data ? (res.data[0].refreshToken as string) : '';
          this.localStorageService.setItem('token', token);
          this.localStorageService.setItem('refreshToken', refresh);
          this.fetchLoggedInUser();
        },
        error: error => {
          this.isAuthLoading = false;
          this.clearCredentialFields();
          if (error?.error?.message) {
            this.toastr.error(error?.error?.message);
          }
        },
      });
    } else {
      this.toastr.error('Credentials not found!');
    }
  }

  ngOnDestroy() {
    this.renderer.removeClass(document.querySelector('app-root'), 'login-page');
  }
}
